import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { toast } from "sonner";
import type { CateringAddon } from "@/hooks/useCateringMenus";

interface EditAddonDialogProps {
  addon: CateringAddon | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (addon: any, type: string, updateID?: string) => void;
}

export function EditAddonDialog({ addon, open, onOpenChange, onSave }: EditAddonDialogProps) {
  const isNew = !addon?.id;
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [pricePerPax, setPricePerPax] = useState("");
  const [flatPrice, setFlatPrice] = useState("");
  const [minQuantity, setMinQuantity] = useState("1");
  const [maxQuantity, setMaxQuantity] = useState("");

  useEffect(() => {
    if (addon) {
      setName(addon.name);
      setDescription(addon.description || "");
      setImageUrl(addon.image_url || "");
      setPricePerPax(addon.price_per_pax?.toString() || "");
      setFlatPrice(addon.flat_price?.toString() || "");
      setMinQuantity(addon.min_quantity?.toString() || "1");
      setMaxQuantity(addon.max_quantity?.toString() || "");
    } else {
      setName("");
      setDescription("");
      setImageUrl("");
      setPricePerPax("");
      setFlatPrice("");
      setMinQuantity("1");
      setMaxQuantity("");
    }
  }, [addon, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        image_url: imageUrl.trim() || null,
        price_per_pax: pricePerPax ? parseFloat(pricePerPax) : null,
        flat_price: flatPrice ? parseFloat(flatPrice) : null,
        min_quantity: minQuantity ? parseInt(minQuantity) : 1,
        max_quantity: maxQuantity ? parseInt(maxQuantity) : null,
      };
      if (isNew) {
        onSave(payload, 'add', '')      
      }else {
        onSave(payload, 'update', addon.id)
      }
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save add-on");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add Add-on" : "Edit Add-on"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Live Station - Satay" />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="space-y-2">
            <Label>Image URL</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
            {imageUrl && (
              <img src={imageUrl} alt="Preview" className="w-16 h-16 rounded object-cover mt-1" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price Per Pax ($)</Label>
              <Input type="number" step="0.01" min="0" value={pricePerPax} onChange={(e) => setPricePerPax(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Flat Price ($)</Label>
              <Input type="number" step="0.01" min="0" value={flatPrice} onChange={(e) => setFlatPrice(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min Quantity</Label>
              <Input type="number" min="0" value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Max Quantity</Label>
              <Input type="number" min="0" value={maxQuantity} onChange={(e) => setMaxQuantity(e.target.value)} placeholder="No limit" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isNew ? "Add Add-on" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
